import { SearchIcon } from '@heroicons/react/solid'
import Fuse from 'fuse.js'
import { useContext, useEffect, useRef, useState } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { FixedSizeList } from 'react-window'
import BaseInput from 'shared/components/base/form/base-input'
import BaseSelect from 'shared/components/base/form/base-select'
import LoadingSpinner from 'shared/components/loading/loading-spinner'
import dictionary from 'shared/constants/dictionary'
import { SocketContext, useEmit } from 'shared/contexts/socket/socket-context'
import {
  selectOperator,
  setSelectedRoleFilter,
} from 'store/slice/call-detail/call-detail.slice'
import OperatorExtension from 'store/slice/call-detail/types'
import { setOperators } from 'store/slice/crmData/crm-data.slice'
import { RootState } from 'store/store'

import Operator from './operator'
import { OrderOperators } from './order-operators'

const OperatorsContainer = () => {
  const socket = useContext(SocketContext)
  const dispatch = useDispatch()
  const emit = useEmit()
  const { operators, productTypes } = useSelector(
    (state: RootState) => state.crmData
  )
  const [options, setOptions] = useState<string[]>([])
  const [search, setSearch] = useState<string>('')
  const [orderedOperators, setOrderedOperators] = useState<OperatorExtension[]>(
    []
  )
  const { formData, selectedRoleFilter, callData } = useSelector(
    (state: RootState) => state.callDetail
  )
  const { selectedOperator } = formData
  const { isHangup } = callData
  const operatorsListRef = useRef<any>()
  const inputRef = useRef<HTMLInputElement>()
  useHotkeys(
    '*',
    (e) => {
      if (e.key === '*' && !isHangup) {
        inputRef?.current?.focus()
        e.preventDefault()
      }
    },
    {
      filter: (e) => {
        // for check box fix
        if (e.key === '*' && !isHangup) {
          inputRef?.current?.focus()
          e.preventDefault()
        }
        return true
      },
    }
  )

  function orderAndFilterOperators() {
    const _operators = [...operators]
    if (search) {
      return setOrderedOperators(handleSearchFilter(_operators))
    }
    let filteredOperators = filterOperatorsBasedOnRole(_operators)
    filteredOperators = OrderOperators(
      filteredOperators,
      productTypes,
      formData.callType
    )
    return setOrderedOperators(filteredOperators)
  }

  function handleSearchFilter(
    _operators: OperatorExtension[]
  ): OperatorExtension[] {
    if (!isNaN(Number(search))) {
      return _operators.filter((_operator) =>
        _operator.userExtension.includes(search)
      )
    }
    // Do A FUSSY SEARCH
    const fuse = new Fuse(_operators, {
      threshold: 0.4,
      ignoreLocation: true,
      keys: [
        'userPosition',
        'fullName',
        'product_role',
        'searchName',
        'userPositionLevel',
      ],
    })
    return fuse.search(search).map((fuseResult) => fuseResult.item)
  }

  function filterOperatorsBasedOnRole(_operators: OperatorExtension[]) {
    if (selectedRoleFilter !== 'all') {
      return _operators.filter(
        (operator) => operator.teamName === selectedRoleFilter
      )
    }
    return _operators
  }

  const scrollToTopOfOperatorsContainer = () => {
    operatorsListRef.current?.scrollToItem?.(0)
  }

  useEffect(() => {
    scrollToTopOfOperatorsContainer()
    orderAndFilterOperators()
  }, [productTypes, operators, formData.callType, search, selectedRoleFilter])

  useEffect(() => {
    switch (formData.transferredBusinessUnit) {
      case 'آهن آنلاین پلاس':
        setSearch('علی رحمدل')
        break
      case 'مدیرعامل':
        setSearch('کارشناس دفتر مدیر عامل')
        break
      case 'تحویل کالا':
        setSearch('کارشناس تحویل کالا')
        break
      default:
        setSearch(formData.transferredBusinessUnit)
        break
    }
  }, [formData.transferredBusinessUnit])

  function populateOperators(extensions: OperatorExtension[]) {
    if (!extensions || !extensions?.length || !Array.isArray(extensions)) {
      return
    }
    setOptions(() => {
      const _options: string[] = []
      const optionSet = new Set(
        extensions.map((extension) => extension.teamName)
      )
      optionSet.forEach((item) => _options.push(item))
      return _options
    })

    dispatch(
      setOperators(
        extensions.map((extension) => new OperatorExtension(extension))
      )
    )
  }

  useEffect(() => {
    emit('get-operators')
      .then((extensions: OperatorExtension[]) => {
        populateOperators(extensions)
      })
      .catch(() => {
        toast.error(dictionary.socket_timeout)
      })
    socket?.on('extensionStatus', (extensions: OperatorExtension[]) => {
      populateOperators(extensions)
    })

    return () => {
      socket?.off('extensionStatus')
    }
  }, [])

  function handleRoleFilterSelect(role: string) {
    dispatch(setSelectedRoleFilter(role))
  }

  const checkToSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault()
    if (search.length === 3 && !Number.isNaN(search)) {
      const foundedOperator = orderedOperators.find(
        (item) => item.userExtension === search
      )
      if (foundedOperator) {
        dispatch(selectOperator(foundedOperator))
        const submitButton = document.getElementById('submit-button')
        if (submitButton) {
          setTimeout(() => {
            submitButton.click()
          }, 100)
        }
      }
    }
  }

  const OperatorRow = ({ index, style }: any) => (
    <div style={{ ...style, right: '0' }}>
      <Operator
        isSelected={selectedOperator?.id === orderedOperators[index]?.id}
        operator={orderedOperators[index]}
        index={index}
        key={index}
      />
    </div>
  )

  return (
    <>
      {isHangup && (
        <div className="absolute inset-0 max-h-[40rem] bg-color-overlay bg-opacity-[48%] z-10 flex justify-center items-center rounded-lg hover:cursor-not-allowed">
          <p className="font-semibold text-sm leading-8 text-white ">
            به دلیل قطع تماس، امکان اتصال به شخص دیکر وجود ندارد
          </p>
        </div>
      )}
      <div className="mb-6 pl-8">
        <form className="grid grid-cols-7 pb-6 gap-2" onSubmit={checkToSubmit}>
          <BaseInput
            className="col-span-4 bg-[#FCFCFC] rounded-lg"
            trailingIcon={SearchIcon}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            ref={inputRef as any}
            id="search-operator"
            disabled={isHangup}
          />
          <BaseSelect<string>
            onSelect={(value) => handleRoleFilterSelect(value)}
            defaultValue={'all'}
            value={selectedRoleFilter}
            className="col-span-3"
            options={[
              { label: 'همه', key: 'all', value: 'all' },
              ...options.map((role) => ({
                key: role,
                label: role,
                value: role,
              })),
            ]}
            disabled={isHangup}
          />
        </form>

        {operators?.length < 1 && (
          <div className="flex justify-center items-center min-h-full">
            <p className="inline text-sm text-red-500 font-medium">
              در حال بارگذاری
            </p>
            <LoadingSpinner className="mr-2" />
          </div>
        )}

        {orderedOperators.length < 1 && operators.length > 0 ? (
          <p className="text-center">موردی یافت نشد</p>
        ) : (
          <FixedSizeList
            ref={operatorsListRef}
            direction="rtl"
            height={464}
            itemSize={100}
            itemCount={orderedOperators?.length}
            width="100%"
            className="overflow-y-visible overflow-x-hidden space-y-3 soft-scrollbar -ml-4 pl-4"
            innerElementType={(props) => (
              <div {...props} className={`relative ${props.className}`}>
                {props.children}
              </div>
            )}
          >
            {OperatorRow}
          </FixedSizeList>
        )}
      </div>
    </>
  )
}

export default OperatorsContainer
